<template>
  <div v-if="isLoading" class="flex items-center justify-center h-screen bg-background text-foreground">
    <div class="fingerprint-spinner">
      <div class="spinner-ring"></div>
      <div class="spinner-ring"></div>
      <div class="spinner-ring"></div>
      <div class="spinner-ring"></div>
      <div class="spinner-ring"></div>
      <div class="spinner-ring"></div>
      <div class="spinner-ring"></div>
      <div class="spinner-ring"></div>
      <div class="spinner-ring"></div>
    </div>
  </div>
  <RouterView v-else />
  <Toaster />
</template>

<script setup lang="ts">
import { ref, onMounted } from 'vue'
import { useAuthStore } from '@/core/auth/auth'
import { useMainStore } from '@/core/store/main'
import { Toaster } from '@/shared/components/ui/toast'

const authStore = useAuthStore()
const mainStore = useMainStore()
const isLoading = ref(true)

onMounted(async () => {
  // Initialize theme synchronously before profile fetch so the loader matches the dark/light state
  const storedTheme = localStorage.getItem('theme') || 'dark'
  document.documentElement.classList.remove('light', 'dark')
  document.documentElement.classList.add(storedTheme)

  // Safety fallback: ensure loading spinner is dismissed even if network/auth profile hangs
  const safetyTimer = setTimeout(() => {
    if (isLoading.value) {
      console.warn('App.vue: Safety timeout reached, releasing initial loader')
      isLoading.value = false
    }
  }, 4000)

  try {
    authStore.checkAuth()
    
    if (authStore.isAuthenticated) {
      try {
        await authStore.fetchProfile()
      } catch (error) {
        console.error('App.vue: Failed to fetch profile on mount:', error)
      }
    }
  } finally {
    clearTimeout(safetyTimer)
    isLoading.value = false
  }
  
  if (authStore.isAuthenticated) {
    // Background helpers — only fetch what this user's permissions need
    const { can } = await import('@/core/permissions/can')
    const tasks: Promise<unknown>[] = []

    // QBO class/location used across sales/customers
    if (can('page.customersLink') || can('page.customerForm') || can('page.receivePayment') || can('page.billing')) {
      tasks.push(mainStore.fetchQboData())
    }
    // Shared catalogs for customer/install forms
    if (can('page.customersLink') || can('page.customerForm') || can('page.installations') || can('page.tasks') || can('action.installations.editForm') || can('action.customer.edit')) {
      tasks.push(mainStore.fetchServices())
    }
    // Deposit accounts for payment pages & branch management (loads from cache + pulls fresh in background)
    if (
      can('page.receivePayment') ||
      can('page.hotspotPayments') ||
      can('page.branches') ||
      can('action.branch.create') ||
      can('action.branch.edit') ||
      authStore.user?.role === 'admin'
    ) {
      tasks.push(mainStore.fetchDepositAccounts({ force: true }))
    }

    if (tasks.length) {
      Promise.allSettled(tasks).catch((error: any) => {
        console.error('App.vue: Error loading global data in background:', error)
      })
    }
  }
})
</script>

<style scoped>
.fingerprint-spinner, .fingerprint-spinner * {
  box-sizing: border-box;
}

.fingerprint-spinner {
  height: 64px;
  width: 64px;
  padding: 2px;
  overflow: hidden;
  position: relative;
}

.fingerprint-spinner .spinner-ring {
  position: absolute;
  border-radius: 50%;
  border: 2px solid transparent;
  border-top-color: hsl(var(--primary));
  animation: fingerprint-spinner-animation 1500ms cubic-bezier(0.680, -0.750, 0.265, 1.750) infinite forwards;
  margin: auto;
  bottom: 0;
  left: 0;
  right: 0;
  top: 0;
}

.fingerprint-spinner .spinner-ring:nth-child(1) {
  height: calc(60px / 9 + 0 * 60px / 9);
  width: calc(60px / 9 + 0 * 60px / 9);
  animation-delay: calc(50ms * 1);
}

.fingerprint-spinner .spinner-ring:nth-child(2) {
  height: calc(60px / 9 + 1 * 60px / 9);
  width: calc(60px / 9 + 1 * 60px / 9);
  animation-delay: calc(50ms * 2);
}

.fingerprint-spinner .spinner-ring:nth-child(3) {
  height: calc(60px / 9 + 2 * 60px / 9);
  width: calc(60px / 9 + 2 * 60px / 9);
  animation-delay: calc(50ms * 3);
}

.fingerprint-spinner .spinner-ring:nth-child(4) {
  height: calc(60px / 9 + 3 * 60px / 9);
  width: calc(60px / 9 + 3 * 60px / 9);
  animation-delay: calc(50ms * 4);
}

.fingerprint-spinner .spinner-ring:nth-child(5) {
  height: calc(60px / 9 + 4 * 60px / 9);
  width: calc(60px / 9 + 4 * 60px / 9);
  animation-delay: calc(50ms * 5);
}

.fingerprint-spinner .spinner-ring:nth-child(6) {
  height: calc(60px / 9 + 5 * 60px / 9);
  width: calc(60px / 9 + 5 * 60px / 9);
  animation-delay: calc(50ms * 6);
}

.fingerprint-spinner .spinner-ring:nth-child(7) {
  height: calc(60px / 9 + 6 * 60px / 9);
  width: calc(60px / 9 + 6 * 60px / 9);
  animation-delay: calc(50ms * 7);
}

.fingerprint-spinner .spinner-ring:nth-child(8) {
  height: calc(60px / 9 + 7 * 60px / 9);
  width: calc(60px / 9 + 7 * 60px / 9);
  animation-delay: calc(50ms * 8);
}

.fingerprint-spinner .spinner-ring:nth-child(9) {
  height: calc(60px / 9 + 8 * 60px / 9);
  width: calc(60px / 9 + 8 * 60px / 9);
  animation-delay: calc(50ms * 9);
}

@keyframes fingerprint-spinner-animation {
  100% {
    transform: rotate(360deg);
  }
}
</style>
